import React, { useEffect, useState } from 'react';
import { CardTitle } from '../../components/Card';
import { useLocation, useNavigate } from 'react-router-dom';
import Style from './deviceManageStyle.module.scss';
import { useAuth } from 'oidc-react';
import { useParams } from 'react-router-dom';
import Project from '../../common/Utils/project';
import { LoaderCircle } from 'lucide-react';
import Pagination from '../../components/Pagination/Pagination';
import { CheckCircle, X } from 'lucide-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { usePermissions } from '../../hooks/usePermissions';
import { useAgentHeartBeatService } from '../../components/services/agentHeartBeatService';
import { useDeviceManagementService } from '../../components/services/deviceManagementService';
import api from '../../components/services/apiService';
// removed useDebounce import as search will be applied only on Enter
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { AgentStatusIndicator } from '../../components/AgentStatus';
import { DataTable, ColumnConfig } from '../../components/Table';
import DeactivateAgentsModal from '../../components/Modal/DeactivateAgentsModal';
import SegmentedControl from '../../components/SegmentedControl/SegmentedControl';

type Device = {
  id: string;
  agentId: string;
  hostName: string;
  ipAddress: string;
  macAddress: string;
  agentVersion: string;
  osName: string;
  groupName: string;
  isAgentActive?: boolean;
  statusMessage?: string;
  isPolicyComplaint?: string;
  isMarkedForDeletion?: boolean;
  estimatedDeletionDateUtc?: string;
};

interface DeviceManage {
  activeEndpoints: number;
  activeServers: number;
  totalDevices: number;
  endpoints: Device[];
}

interface DeviceManagementBanner {
  totalEndpoints: number;
  totalServers: number;
  activeEndpoints: number;
  activeServers: number;
  totalDevices: number;
  deviceHealthPercentage: number;
}

const statusColor = {
  true: 'bg-green-500',
  false: 'bg-red-500',
  idle: 'bg-gray-400',
};

const itemsPerPage = 20;

interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

const UsersTable = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToast();

  // ✅ Get search & active filter from URL params
  const queryParams = new URLSearchParams(location.search);
  const searchTerm = queryParams.get('search') || '';
  const showActiveOnly = queryParams.get('isActive') === 'true';

  // Local input state separate from URL; synced with URL changes (back/forward, selection)
  const [searchInput, setSearchInput] = useState<string>(searchTerm);

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [statusesLoading, setStatusesLoading] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [deviceManage, setDeviceManage] = useState<DeviceManage>({
    activeEndpoints: 0,
    activeServers: 0,
    totalDevices: 0,
    endpoints: [],
  });
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [action, setAction] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [showDeactivateModal, setShowDeactivateModal] = useState<boolean>(false);
  const [markForPermanentDeletion, setMarkForPermanentDeletion] = useState<boolean>(false);
  const { selectedTenantId } = useTenantSelection();
  const auth = useAuth();
  const { deviceHealth } = useParams();
  const { selectedTenantId: tenantId } = useTenantSelection();
  const { getAgentHeartbeatStatus } = useAgentHeartBeatService();
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'offline' | 'deleted'>('all');
  const { canEdit } = usePermissions();
  const canManageDevices = canEdit('deviceManagement');

  const getCurrentActor = () => {
    const profile = auth.userData?.profile as Record<string, string | undefined> | undefined;

    return profile?.email ?? profile?.preferred_username ?? profile?.upn ?? profile?.name ?? 'system';
  };

  // Get device list with proper typing
  const deviceList: Device[] = deviceManage.endpoints || [];
  const options = [
    { val: 'all', label: 'All', dot: 'bg-gray-500' },
    { val: 'online', label: 'Online', dot: 'bg-green-500' },
    { val: 'offline', label: 'Offline', dot: 'bg-red-500' },
    { val: 'deleted', label: 'Deactivated', dot: 'bg-orange-500' },
  ];

  const handleStatusFilter = (value: 'all' | 'online' | 'offline' | 'deleted') => {
    const params = new URLSearchParams(location.search);

    params.delete('isOnline');
    params.delete('isMarkedForDeletion');

    if (value === 'online' || value === 'offline') {
      params.set('isOnline', value === 'online' ? 'true' : 'false');
    } else if (value === 'deleted') {
      params.set('isMarkedForDeletion', 'true');
    }

    params.delete('page');
    setCurrentPage(1);
    setStatusFilter(value);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const totalPages = paginationInfo?.totalPages ?? 1;

  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const [items, setItems] = useState([
    { id: 1, autoUpdate: false },
    { id: 2, autoUpdate: true },
  ]);

  // Apply search to URL ONLY when Enter is pressed
  const applySearch = () => {
    const value = searchInput;
    const params = new URLSearchParams(location.search);

    if (value.trim()) {
      params.set('search', value.trim());
    } else {
      params.delete('search');
    }

    // Reset to page 1 when searching
    params.delete('page');
    setCurrentPage(1);

    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    setShowDropdown(true);
  };

  // Input only updates local state (typing won't trigger API)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    setShowDropdown(true);
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedItemIds([]);
    } else {
      setSelectedItemIds(deviceList.map((d) => d.agentId));
    }
    setAllSelected((s) => !s);
  };

  // Selecting from dropdown still updates URL → triggers API call
  const handleSelect = (value: string) => {
    const params = new URLSearchParams(location.search);
    params.set('search', value);
    params.delete('page');

    navigate(`${location.pathname}?${params.toString()}`);
    setShowDropdown(false);
    setCurrentPage(1);
  };

  const routeInfo = (id: string) => {
    navigate(`/deviceManagement/info/${id}`);
  };

  const fetchAndMergeStatuses = async (endpoints: Device[]) => {
    if (!endpoints || endpoints.length === 0) return;

    try {
      setStatusesLoading(true);
      // Agents marked for deletion are on their way out — skip the heartbeat call for them entirely.
      const activeEndpointsForHeartbeat = endpoints.filter((device) => !device.isMarkedForDeletion);
      const agentIds = activeEndpointsForHeartbeat.map((device) => device.agentId);
      const heartbeatResponse = agentIds.length > 0 ? await getAgentHeartbeatStatus(selectedTenantId as string, agentIds) : null;
      const statusData = heartbeatResponse?.isSuccess && Array.isArray(heartbeatResponse.data) ? heartbeatResponse.data : [];

      const enrichedEndpoints = endpoints.map((device) => {
        if (device.isMarkedForDeletion) {
          return device;
        }
        const agent = statusData.find((item: any) => item.tenantAgentId === device.agentId);
        return {
          ...device,
          isAgentActive: agent?.agentStatus === 'Active',
          statusMessage: agent?.heartbeatStatusMessage || '',
        };
      });

      setDeviceManage((prev) => ({
        ...prev,
        endpoints: enrichedEndpoints,
      }));
    } catch (error) {
      console.error('Error fetching agent statuses:', error);
    } finally {
      setStatusesLoading(false);
    }
  };

  const getDeviceManagement = async (page = 1) => {
    const queryParams = new URLSearchParams(location.search);
    const criteria = queryParams.get('action');
    const groupId = queryParams.get('groupId');
    const searchParam = queryParams.get('search'); // ✅ Get search from URL
    const isOnlineParam = queryParams.get('isOnline'); // ✅ Get active filter from URL
    const isMarkedForDeletionParam = queryParams.get('isMarkedForDeletion'); // ✅ Get marked-for-deletion filter from URL
    try {
      setTableLoading(true);
      const token = auth?.userData?.access_token;
      const tenantId = selectedTenantId;

      // Prepare pagination & search params
      const params = new URLSearchParams({
        pageNumber: page.toString(),
        pageSize: itemsPerPage.toString(),
      });

      // ✅ Use search from URL params
      if (searchParam && searchParam.trim()) {
        params.append('search', searchParam.trim());
      }

      // ✅ Add isOnline filter if set
      if (isOnlineParam) {
        params.set('isOnline', isOnlineParam);
      }

      // ✅ Add isMarkedForDeletion filter if set
      if (isMarkedForDeletionParam) {
        params.set('isMarkedForDeletion', isMarkedForDeletionParam);
      }

      // ✅ Add tenantGroupId if groupId exists
      if (groupId && groupId.trim() !== '') {
        params.append('tenantGroupId', groupId);
      }

      // Build base API URL
      let endpointUrl = `${Project.api}agentapi/AgentDeviceManagement/${tenantId}/agent-endpoints`;
      if (criteria) {
        endpointUrl += `/${criteria}`;
      }

      // Fetch device management data
      const response = await api.get(`${endpointUrl}?${params.toString()}`);
      const deviceManagementData = response?.data?.data;

      // ✅ Handle empty or malformed responses
      if (!deviceManagementData || !Array.isArray(deviceManagementData.endpoints)) {
        setDeviceManage({
          activeEndpoints: 0,
          activeServers: 0,
          totalDevices: 0,
          endpoints: [],
        });
        setPaginationInfo(null);
        return;
      }

      // ✅ Update state with initial data
      setDeviceManage({
        ...deviceManagementData,
        endpoints: deviceManagementData.endpoints,
      });
      setPaginationInfo(response?.data?.pagination ?? null);

      // ✅ Fetch and merge agent statuses
      await fetchAndMergeStatuses(deviceManagementData.endpoints);
    } catch (error) {
      console.error('Error fetching device management data:', error);
      setDeviceManage({
        activeEndpoints: 0,
        activeServers: 0,
        totalDevices: 0,
        endpoints: [],
      });
      setPaginationInfo(null);
    } finally {
      setTableLoading(false);
    }
  };

  const performAgentSync = async () => {
    try {
      if (auth && auth.userData) {
        setLoading(true);

        const payload = {
          agentIdList: selectedItemIds,
        };
        const response = await api.post(
          `${Project.api}patchcenterservice/RemediationPlan/${tenantId}/endpoint-sync-list`,
          JSON.stringify(payload),
        );

        let { data } = response;
        if (data?.isSuccess) {
          setIsSuccess(true);
          setLoading(false);
          // setSuccessMsg('Agent syncronization completed successfully');
          addToast('Agent synchronization initiated successfully', 'success', 3000, 'Agent Sync');
        }
      }
    } catch (error) {
      console.error('Error Agent syncronization:', error);
      setIsSuccess(false);
      setLoading(false);
    }
  };

  const deSelectAgents = () => {
    setSelectedItemIds([]);
    setAllSelected(false);
  };
  const performReboot = async () => {
    try {
      if (auth && auth.userData) {
        setLoading(true);

        const payload = {
          agentIdList: selectedItemIds,
        };
        const response = await api.post(
          `${Project.api}patchcenterservice/RemediationPlan/${tenantId}/reboot-device-list`,
          JSON.stringify(payload),
        );

        let { data } = response;
        if (data?.isSuccess) {
          setIsSuccess(true);
          setLoading(false);
          // setSuccessMsg('Reboot completed successfully');
          addToast('Reboot initiated successfully', 'success', 3000, 'Reboot');
        }
      }
    } catch (error) {
      console.error('Error Reboot:', error);
      setIsSuccess(false);
      setLoading(false);
    }
  };

  const performSwitchOff = async () => {
    try {
      if (auth && auth.userData) {
        setLoading(true);

        const payload = {
          agentIdList: selectedItemIds,
        };
        const response = await api.post(
          `${Project.api}patchcenterservice/RemediationPlan/${tenantId}/shutdown-device-list`,
          JSON.stringify(payload),
        );

        let { data } = response;
        if (data?.isSuccess) {
          setIsSuccess(true);
          setLoading(false);
          // setSuccessMsg('Switch off completed successfully');
          addToast('Switch off initiated successfully', 'success', 3000, 'Switch Off');
        }
      }
    } catch (error) {
      console.error('Error Switch off:', error);
      setIsSuccess(false);
      setLoading(false);
    }
  };

  const deactiveAgents = async () => {
    try {
      if (auth && auth.userData) {
        setLoading(true);
        const actor = getCurrentActor();

        const response = await api.put(
          `${Project.api}tenantservice/TenantAgent/${tenantId}/disable-tenant-agents`,
          JSON.stringify({ agentIds: selectedItemIds, agentStatusChangedBy: actor }),
        );

        let { data } = response;
        if (data?.isSuccess) {
          setIsSuccess(true);
          addToast('De-activation initiated successfully', 'success', 3000, 'De-activate Agents');
        }
      }
    } catch (error) {
      console.error('Error de-activate agent:', error);
      setIsSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const markAgentsForDeletion = async () => {
    try {
      if (auth && auth.userData) {
        setLoading(true);
        const actor = getCurrentActor();

        const response = await api.put(
          `${Project.api}tenantservice/TenantAgent/${tenantId}/mark-tenant-agents-for-deletion`,
          JSON.stringify({ agentIds: selectedItemIds, deletedBy: actor }),
        );

        let { data } = response;
        if (data?.isSuccess) {
          setIsSuccess(true);
          addToast('Agents marked for permanent deletion successfully', 'success', 3000, 'Delete Agents');
        }
      }
    } catch (error) {
      console.error('Error marking agents for permanent deletion:', error);
      setIsSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const getSquareStyle = (status: boolean | 'indeterminate') => {
    let backgroundColor = 'transparent';

    if (status === true) {
      backgroundColor = '#00FF00';
    } else if (status === 'indeterminate') {
      backgroundColor = '#FFA500';
    }

    return {
      width: '20px',
      height: '20px',
      borderRadius: '4px',
      border: status ? '#00FF00' : '5px solid #444',
      backgroundColor,
      cursor: 'pointer',
      display: 'inline-block',
      marginLeft: '35px',
    };
  };

  const handleToggle = (agentId: string) => {
    setSelectedItemIds((prev) => (prev.includes(agentId) ? prev.filter((x) => x !== agentId) : [...prev, agentId]));
  };

  const handleAction = () => {
    if (selectedItemIds.length === 0) {
      addToast('Please select at least one agent.', 'error', 3000, 'Device Center');
      return;
    }

    const hasMarkedForDeletionAgent = deviceList.some((device) => selectedItemIds.includes(device.agentId) && device.isMarkedForDeletion);
    if (hasMarkedForDeletionAgent) {
      addToast(
        'Your selection includes one or more agents marked for deletion. Deselect them before performing this action.',
        'warning',
        4000,
        'Device Center',
      );
      return;
    }

    switch (action) {
      case 'agent-sync':
        performAgentSync();
        deSelectAgents();
        break;
      case 'reboot':
        performReboot();
        deSelectAgents();
        break;
      case 'switch-off':
        performSwitchOff();
        deSelectAgents();
        break;
      case 'deactivate-agents':
        setMarkForPermanentDeletion(false);
        setShowDeactivateModal(true);
        break;
    }
  };

  const confirmDeactivateAction = async () => {
    if (markForPermanentDeletion) {
      await markAgentsForDeletion();
    } else {
      await deactiveAgents();
    }

    setShowDeactivateModal(false);
    setMarkForPermanentDeletion(false);
    deSelectAgents();
  };

  const [deviceManagementBanner, setDeviceManagementBanner] = useState<DeviceManagementBanner | undefined>();
  const { getDeviceManagementBanner } = useDeviceManagementService();

  const getBanner = async () => {
    try {
      if (auth && auth.userData) {
        const token = auth.userData.access_token;
        const response = await getDeviceManagementBanner(selectedTenantId as string);

        let { data } = response;
        setDeviceManagementBanner(data);
      }
    } catch (error) {
      console.error('Error fetching device management banner:', error);
      return [];
    }
  };

  const downloadCSV = async () => {
    try {
      if (auth && auth.userData) {
        setLoading(true);

        const token = auth.userData.access_token;
        const tenantId = selectedTenantId;
        const response = await api.get(
          `${Project.api}agentapi/AgentDeviceManagement/${tenantId}/agent-endpoints${deviceHealth ? '/' + deviceHealth : ''}`,
        );

        const { data } = response;

        // Pick only required columns
        const rows = data.data.endpoints?.map((item: Device) => ({
          'Host Name': item.hostName,
          'IP Address': item.ipAddress,
          Version: item.agentVersion,
          'Operating System': item.osName,
          'Group Name': item.groupName,
          'Complaint Status': item.isPolicyComplaint || 'false',
        }));

        // Convert to CSV string
        const header = Object.keys(rows[0]).join(',');
        const csv = [
          header,
          ...rows.map((row: any) =>
            Object.values(row)
              .map((value) => `"${value ?? ''}"`)
              .join(','),
          ),
        ].join('\n');

        // Create blob and trigger download
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', 'devices.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Failed to download CSV', error);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 For table data (depends on pagination & search from URL)
  useEffect(() => {
    if (auth && auth.userData) {
      getDeviceManagement(currentPage);
    }
  }, [auth, currentPage, location.search]); // removed debouncedSearch dependency

  // 🔹 For banner data (runs only once)
  useEffect(() => {
    if (auth && auth.userData) {
      getBanner();
    }
  }, [auth]);
  const columns: ColumnConfig[] = [
    {
      key: 'select',
      header: <div onClick={handleSelectAll} style={getSquareStyle(allSelected)} />,
      renderHeader: () => <div onClick={handleSelectAll} style={getSquareStyle(allSelected)} />,
      width: '6%',
      render: (_: any, row: Device) => (
        <div onClick={() => handleToggle(row.agentId)} style={getSquareStyle(selectedItemIds.includes(row.agentId))} />
      ),
      align: 'center',
    },
    {
      key: 'hostName',
      header: 'Host Name',
      renderHeader: () => <span style={{ display: 'block', width: '100%', paddingLeft: 8 }}>Host Name</span>,
      width: '13%',
      render: (value: any, row: Device) => (
        <span style={{ cursor: 'pointer', paddingLeft: 8 }} onClick={() => routeInfo(row.agentId)}>
          {row.hostName}
        </span>
      ),
    },
    {
      key: 'ipAddress',
      header: 'IP Address',
      renderHeader: () => <span style={{ display: 'block', width: '100%' }}>IP Address</span>,
      width: '13%',
      render: (value) => value,
    },
    {
      key: 'agentVersion',
      header: 'Version',
      renderHeader: () => <span style={{ display: 'block', width: '100%' }}>Version</span>,
      width: '10%',
      render: (value) => value,
    },
    {
      key: 'osName',
      header: 'Operating System',
      renderHeader: () => <span style={{ display: 'block', width: '100%' }}>Operating System</span>,
      width: '10%',
      render: (value) => value,
    },
    {
      key: 'groupName',
      header: 'Group Name',
      renderHeader: () => <span style={{ display: 'block', width: '100%' }}>Group Name</span>,
      width: '16%',
      render: (value) => value,
    },
    {
      key: 'isAgentActive',
      header: 'Current Status',
      renderHeader: () => <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Current Status</span>,
      width: '11%',
      render: (_: any, row: Device) => (
        <AgentStatusIndicator
          agentId={row.agentId}
          isActive={row.isAgentActive}
          statusMessage={row.statusMessage}
          isLoading={statusesLoading}
          isMarkedForDeletion={row.isMarkedForDeletion}
          estimatedDeletionDateUtc={row.estimatedDeletionDateUtc}
        />
      ),
      align: 'center',
    },
    {
      key: 'isPolicyComplaint',
      header: 'Complaint Status',
      renderHeader: () => <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Complaint Status</span>,
      cellStyle: { justifyContent: 'center' },
      width: '11%',
      render: (value, row: Device) =>
        row.isPolicyComplaint === undefined ? (
          <span className={`inline-block h-3 w-3 rounded-full mx-auto ${Style.blinkColor}`}></span>
        ) : (
          <span className={`inline-block w-3 h-3 rounded-full mx-auto ${statusColor[row.isPolicyComplaint ? 'true' : 'false']}`}></span>
        ),
      align: 'center',
    },
  ];

  return (
    <div className="p-6 text-white min-h-screen">
      {/* Top Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Active Endpoints', value: deviceManagementBanner?.activeEndpoints },
          { label: 'Active Servers', value: deviceManagementBanner?.activeServers },
          { label: 'Total Devices', value: deviceManagementBanner?.totalDevices },
          { label: 'Device Health %', value: deviceManagementBanner?.deviceHealthPercentage },
        ]?.map((card, i) => (
          <div
            key={i}
            style={{ backgroundColor: i % 2 === 0 ? '#2E2D2D' : '#1D1D1D' }}
            className="h-[120px] p-4 rounded-lg shadow flex flex-col br-20"
          >
            <CardTitle title={card.label} />
            <span className="text-2xl font-semibold mt-[25px] ml-[28px]">{card.value}</span>
          </div>
        ))}
      </div>

      {/* {isSuccess && (
        <div className="flex items-center justify-between p-4 text-green-600 bg-green-100 rounded">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMsg}</span>
          </div>
          <button onClick={() => setIsSuccess(false)} className="ml-4 text-green-700 hover:text-green-900">
            <X className="w-4 h-4" />
          </button>
        </div>
      )} */}

      {/* Search & Buttons */}
      <div className="flex justify-between items-center mb-4">
        <div className="relative w-[260px]">
          <div className="flex items-center bg-[#1C1C1D] px-3 py-2 rounded text-white">
            <input
              type="text"
              placeholder="Search"
              className="bg-transparent outline-none w-full"
              value={searchInput}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  applySearch();
                }
              }}
            />
          </div>

          {showDropdown && searchInput && deviceList.length > 0 && (
            <ul className="absolute z-10 w-full bg-[#1C1C1D] text-white mt-1 border border-gray-700 rounded max-h-60 overflow-auto">
              {deviceList?.map((item, i) => (
                <li
                  key={`${item.hostName}-${i}`}
                  className="px-4 py-2 hover:bg-gray-700 cursor-pointer"
                  onClick={() => handleSelect(item.hostName)}
                >
                  {item.hostName} — {item.ipAddress}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center gap-4 whitespace-nowrap">
          {/* Segmented control */}
          <SegmentedControl options={options} value={statusFilter} onChange={handleStatusFilter} />

          {/* Download */}
          <button className="bg-green-600 px-4 py-2 rounded text-sm br-15 flex-shrink-0" onClick={downloadCSV}>
            Download CSV
          </button>

          {/* Actions */}
          {canManageDevices && (
            <div className="flex items-center gap-3 flex-shrink-0">
              <select
                className="bg-[#1C1C1D] text-white border border-white rounded px-4 py-2 text-sm focus:outline-none"
                onChange={(e) => setAction(e.target.value)}
                defaultValue=""
              >
                <option value="" disabled hidden>
                  Select
                </option>
                <option value="agent-sync">Agent Sync</option>
                <option value="reboot">Reboot</option>
                <option value="switch-off">Switch Off</option>
                <option value="deactivate-agents">Deactivate Agents</option>
              </select>

              <button className="bg-red-600 px-4 py-2 rounded text-sm br-15" onClick={handleAction}>
                Submit {selectedItemIds.length > 0 ? `(${selectedItemIds.length})` : ''}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto rounded-lg shadow">
        <DataTable
          columns={columns}
          data={deviceList}
          loading={tableLoading}
          alternateRowColors={true}
          hoverEffect={true}
          containerStyle={{ background: 'transparent', borderRadius: 0, boxShadow: 'none' }}
          headerStyle={{
            backgroundColor: '#161617',
            // color: '#C9CDD3',
            fontSize: 13,
            fontWeight: 700,
            padding: '0.75rem 0',
            borderBottom: '2px solid #2a2a2a',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            minHeight: 56,
            alignItems: 'center',
          }}
          rowStyle={{
            fontSize: 15,
            padding: '1.25rem 0',
            borderBottom: '1px solid #3a3a3a',
            minHeight: 60,
            cursor: 'pointer',
          }}
        />
        {!tableLoading && deviceList.length === 0 && <div className="py-12 text-center text-gray-400">No matching records</div>}
      </div>

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} loading={tableLoading} onPageChange={handlePageChange} />

      <DeactivateAgentsModal
        show={showDeactivateModal}
        markForPermanentDeletion={markForPermanentDeletion}
        onClose={() => {
          setShowDeactivateModal(false);
          setMarkForPermanentDeletion(false);
        }}
        onConfirm={confirmDeactivateAction}
        onMarkForPermanentDeletionChange={setMarkForPermanentDeletion}
      />
    </div>
  );
};

export default UsersTable;
